import "./App.css";
import PrivateRoute from "./helpers/PrivateRoute";
import { useEffect, useState } from "react";
import { useKeycloak } from "@react-keycloak/web";
import { Outlet } from "react-router-dom";
import ErrorCard from "./components/common/errorStates/ErrorCard";
import { Suspense } from "react";
import { Toaster } from "./components/ui/toaster";
import GlobalLoader from "./components/ui/global-loader";
import { usePostHog } from "posthog-js/react";
import { useGlobalStore } from "./GlobalStore";
import { getUserProfile } from "@/api/globalApiService";
import Navbar from "./components/hoc/Navbar";
import useAccountsList from "./hooks/useAccountsList";
import { initializeGlobalStreams } from "@/services/streamInitializer";
import * as Sentry from "@sentry/react";

// Type declaration for Pylon
declare global {
  interface Window {
    pylon?: {
      chat_settings: {
        app_id: string;
        email: string;
        name: string;
        avatar_url?: string;
        email_hash?: string;
      };
    };
  }
}

function App() {
  const { keycloak } = useKeycloak();
  const postHog = usePostHog();

  // Use selectors to subscribe only to needed state
  const accountList = useGlobalStore((state) => state.accountList);
  const setUserProfile = useGlobalStore((state) => state.setUserProfile);
  const setNoAccountsAvailable = useGlobalStore((state) => state.setNoAccountsAvailable);
  const noAccountsAvailable = useGlobalStore((state) => state.noAccountsAvailable);
  const userEmail = useGlobalStore((state) => state.userEmail);
  const userName = useGlobalStore((state) => state.userName);
  const profilePicture = useGlobalStore((state) => state.profilePicture);
  const supportHash = useGlobalStore((state) => state.supportHash);
  const { isInitializingAccounts, initializeAccounts } = useAccountsList();
  const loadingAccountsList = accountList.length === 0 && isInitializingAccounts;
  const [loadingUserProfile, setLoadingUserProfile] = useState(true);

  useEffect(() => {
    // Initialize global streams for background processing
    initializeGlobalStreams();
  }, []);

  useEffect(() => {
    if (keycloak.idTokenParsed) {
      postHog.identify(keycloak.idTokenParsed?.email as string, {
        email: keycloak.idTokenParsed?.email as string,
        name: keycloak.idTokenParsed?.name as string,
        // Add browser and environment info for debugging
        user_agent: navigator.userAgent,
        screen_resolution: `${window.screen.width}x${window.screen.height}`,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        language: navigator.language,
        environment: import.meta.env.MODE,
      });
      getUserProfile()
        .then((res) => {
          setUserProfile({
            userName: keycloak.idTokenParsed?.name as string,
            userEmail: res.email,
            userCompany: res.company,
            userRole: res.role,
            profilePicture: res.profile_picture,
            llmType: res.llm_type,
            freeTrialExpired: res?.free_trial_expired ?? false,
            permission: res?.permission ?? {},
            supportHash: res?.support_hash ?? null,
          });

          // Update PostHog with full user profile after fetching
          postHog.setPersonProperties({
            email: res.email,
            name: res.name,
            company: res.company,
            role: res.role,
            llm_type: res.llm_type,
            free_trial_expired: res?.free_trial_expired ?? false,
          });

          Sentry.setUser({
            name: res.name,
            email: res.email,
            company: res.company,
            role: res.role,
          });

          setTimeout(() => void initializeAccounts(), 100);
          setLoadingUserProfile(false);
        })
        .catch(() => {});
    }
  }, [keycloak.idTokenParsed, postHog, setUserProfile, initializeAccounts]);

  // watch account list to reverse noAccountsAvailable in case accounts got added or removed
  useEffect(() => {
    if (accountList.length > 0 && noAccountsAvailable) {
      setNoAccountsAvailable(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountList]);

  useEffect(() => {
    postHog.startSessionRecording();
  }, [postHog]);

  useEffect(() => {
    if (keycloak.authenticated && userEmail) {
      const APP_ID = "236bc5ba-e547-434f-b905-b91213d85746";
      window.pylon = {
        chat_settings: {
          app_id: APP_ID,
          email: userEmail,
          name: userName,
          avatar_url: profilePicture || undefined,
          email_hash: supportHash || undefined,
        },
      };
    }
  }, [keycloak.authenticated, userEmail, userName, profilePicture, supportHash]);

  if (loadingAccountsList) return null;
  return (
    <PrivateRoute>
      <Toaster position="top-right" className="right-[14px]" />
      {/* Don't remove id="getcrux-app-root" from the root element. It is used to hide the loader when the app is mounted. */}
      <div id="getcrux-app-root" className="flex min-w-dvw min-h-dvh">
        {/* Nav */}
        <Navbar />
        {/* Outlet */}
        <Sentry.ErrorBoundary fallback={(props) => <ErrorCard {...props} />}>
          <div className="flex-1 h-dvh w-full overflow-hidden pl-[42px]">
            <Suspense>
              {loadingUserProfile ? <GlobalLoader showProgressBar={true} /> : <Outlet />}
            </Suspense>
          </div>
        </Sentry.ErrorBoundary>
      </div>
    </PrivateRoute>
  );
}

export default App;
